import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import Pagination from '../../common/Pagination';
import { getPostUrl } from '../../../utils/urlHelpers';
import postService from '../../../services/postService';
import { formatDate } from '../../../utils/helpers';
import { getPlainTextPreview } from '../../../utils/htmlSanitizer';
import SocietySectionToolbar from './SocietySectionToolbar';
import SectionFilterSelect from './SectionFilterSelect';

const SocietyPosts = ({ society, onError }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    sort: 'newest'
  });

  // Load posts
  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
          search: filters.search,
          category: filters.category !== 'all' ? filters.category : undefined,
          ordering: filters.sort === 'newest' ? '-created_at' : filters.sort,
          society: society.id,
          is_public: true
        };

        const response = await postService.getPosts(params);
        const postsData = response.results || response;
        
        setPosts(postsData);
        setTotalPages(response.total_pages || 1);
        setTotalCount(response.count || postsData.length);
      } catch (error) {
        console.error('Error loading posts:', error);
        onError('Failed to load posts');
      } finally {
        setLoading(false);
      }
    };

    if (society?.id) {
      loadPosts();
    }
  }, [society?.id, currentPage, filters, onError]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };


  const getCategoryColor = (category) => {
    const colors = {
      news: 'bg-blue-100 text-blue-800',
      technology: 'bg-purple-100 text-purple-800',
      business: 'bg-green-100 text-green-800',
      health: 'bg-red-100 text-red-800',
      lifestyle: 'bg-pink-100 text-pink-800',
      entertainment: 'bg-yellow-100 text-yellow-800',
      sports: 'bg-orange-100 text-orange-800',
      education: 'bg-indigo-100 text-indigo-800',
      opinion: 'bg-teal-100 text-teal-800',
      qa: 'bg-cyan-100 text-cyan-800',
      travel: 'bg-emerald-100 text-emerald-800',
      visa: 'bg-sky-100 text-sky-800',
      recipes: 'bg-amber-100 text-amber-800',
      announcements: 'bg-violet-100 text-violet-800',
      local: 'bg-lime-100 text-lime-800',
      deals: 'bg-rose-100 text-rose-800',
      other: 'bg-slate-100 text-slate-800',
    };
    return colors[category] || colors.other;
  };

  const activeFilterCount =
    (filters.category !== 'all' ? 1 : 0) + (filters.sort !== 'newest' ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <SocietySectionToolbar
        title="Posts"
        count={totalCount}
        itemLabel={{ singular: 'post', plural: 'posts' }}
        societyName={society.name}
        searchPlaceholder="Search posts..."
        searchValue={filters.search}
        onSearchChange={(v) => handleFilterChange({ ...filters, search: v })}
        activeFilterCount={activeFilterCount}
        addButton={
          isAuthenticated
            ? {
                label: 'Create Post',
                onClick: () =>
                  navigate(`/societies/${society.slug}/posts/create?society=${society.id}`),
              }
            : null
        }
      >
        <SectionFilterSelect
          label="Category"
          value={filters.category}
          onChange={(e) => handleFilterChange({ ...filters, category: e.target.value })}
        >
          <option value="all">All Categories</option>
          <option value="announcement">Announcements</option>
          <option value="news">News</option>
          <option value="maintenance">Maintenance</option>
          <option value="event">Events</option>
          <option value="general">General</option>
        </SectionFilterSelect>
        <SectionFilterSelect
          label="Sort By"
          value={filters.sort}
          onChange={(e) => handleFilterChange({ ...filters, sort: e.target.value })}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="most_liked">Most Liked</option>
          <option value="most_commented">Most Commented</option>
        </SectionFilterSelect>
      </SocietySectionToolbar>

      {/* Posts List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="medium" />
          </div>
        ) : posts.length > 0 ? (
          posts.map((post) => (
            <div 
              key={post.id} 
              className="bg-white rounded-lg shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(getPostUrl(post, society.slug))}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                    {post.user_avatar ? (
                      <img
                        src={post.user_avatar}
                        alt={post.user_name || 'User'}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center">
                      <h3 className="font-medium text-gray-900">{post.user_name || 'Unknown User'}</h3>
                    </div>
                    <p className="text-sm text-gray-500">{formatDate(post.created_at)}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(post.category)}`}>
                  {post.category.charAt(0).toUpperCase() + post.category.slice(1).replace('_', ' ')}
                </span>
              </div>

              <h4 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h4>
              <p className="text-gray-700 mb-4 line-clamp-3">
                {getPlainTextPreview(post.content, 280)}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center text-gray-500">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    {post.likes_count || 0}
                  </span>
                  <span className="flex items-center text-gray-500">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    {post.comments_count || 0}
                  </span>
                </div>
                <span className="text-blue-600 hover:text-blue-700 font-medium">
                  Read More →
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No posts yet</h3>
            <p className="text-gray-500 mb-4">Be the first to share something with the community.</p>
            {isAuthenticated && (
              <button 
                onClick={() => navigate(`/societies/${society.slug}/posts/create?society=${society.id}`)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
              >
                Create First Post
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default SocietyPosts;
